import React, { useContext, useState } from 'react'
import PropTypes from 'prop-types'
import { useMutation } from '@apollo/client'
import Box from '@material-ui/core/Box'
import Chip from '@material-ui/core/Chip'
import TableCell from '@material-ui/core/TableCell'
import TableRow from '@material-ui/core/TableRow'
import Typography from '@material-ui/core/Typography'
import Avatar from '@material-ui/core/Avatar'
import { makeStyles } from '@material-ui/styles'
import LoadingButton from 'components/loading-button'
import EnhancedTable from 'components/tables/enhanced-table'
import { getInitials } from 'lib/text-display'
import { RefetchContext } from 'lib/utils/refetchContext'
import { SEND_GROUP_EVENT_REGISTRATION_INVITE } from './queries'
import { getRegistrationInviteByContactId } from './utils'

const useRowStyles = makeStyles(theme => ({
  info: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    minWidth: 0,
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  avatar: {
    height: 36,
    width: 36,
    flexShrink: 0,
    backgroundColor: '#EBF0F2',
    color: '#172024',
    fontSize: '13px',
    fontWeight: 500,
  },
  text: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0.15px',
  },
  textSecondary: {
    color: theme.palette.text.secondary,
    fontSize: '12px',
    lineHeight: '16px',
  },
  inviteCell: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: '12px',
    lineHeight: '16px',
  },
}))

const INVITE_STATUS_LABELS = {
  pending: 'Invite sent',
  accepted: 'Accepted',
  expired: 'Expired',
}

const columns = [
  {
    id: 'name',
    label: 'Name',
    align: 'left',
    width: '50%',
    mobile: true,
    sortable: false,
  },
  {
    id: 'email',
    label: 'Email',
    align: 'left',
    width: '50%',
    mobile: true,
    sortable: false,
  },
]

const columnsWithInvites = [
  { ...columns[0], width: '40%' },
  { ...columns[1], width: '30%' },
  {
    id: 'invite',
    label: 'Invite',
    align: 'left',
    width: '30%',
    mobile: true,
    sortable: false,
  },
]

const WaitlistInviteCell = ({ groupEventId, contactId, invite }) => {
  const classes = useRowStyles()
  const refetch = useContext(RefetchContext)
  const [errorMessage, setErrorMessage] = useState(null)

  const [sendInvite, { loading }] = useMutation(
    SEND_GROUP_EVENT_REGISTRATION_INVITE,
    {
      onCompleted: ({ sendGroupEventRegistrationInvite }) => {
        if (sendGroupEventRegistrationInvite.success) {
          setErrorMessage(null)
          refetch?.()
        } else {
          setErrorMessage(sendGroupEventRegistrationInvite.message)
        }
      },
      onError: () =>
        setErrorMessage('Could not send the invite. Please try again.'),
    },
  )

  const canSend = !invite || invite.status === 'pending'

  return (
    <Box>
      <Box className={classes.inviteCell}>
        {invite && (
          <Chip
            label={INVITE_STATUS_LABELS[invite.status]}
            color={invite.status === 'accepted' ? 'primary' : 'default'}
          />
        )}
        {canSend && (
          <LoadingButton
            buttonSize="small"
            variant="outlined"
            color="primary"
            loading={loading}
            onClick={() =>
              sendInvite({ variables: { groupEventId, contactId } })
            }
          >
            {invite ? 'Resend' : 'Send invite'}
          </LoadingButton>
        )}
      </Box>
      {errorMessage && (
        <Typography className={classes.errorText}>{errorMessage}</Typography>
      )}
    </Box>
  )
}

const WaitlistListItem = ({ entry, groupEventId, invite, invitesEnabled }) => {
  const classes = useRowStyles()
  const { firstName, lastName, email } = entry.contact
  const contactName = `${firstName} ${lastName}`

  return (
    <TableRow data-testid={`waitlist-list-row-${entry.contact.id}`}>
      <TableCell className={classes.info}>
        <Box className={classes.nameCell}>
          <Avatar className={classes.avatar}>{getInitials(contactName)}</Avatar>
          <Typography className={classes.text} noWrap>
            {contactName}
          </Typography>
        </Box>
      </TableCell>
      <TableCell className={classes.info}>
        <Typography className={classes.textSecondary} noWrap>
          {email}
        </Typography>
      </TableCell>
      {invitesEnabled && (
        <TableCell className={classes.info}>
          <WaitlistInviteCell
            groupEventId={groupEventId}
            contactId={entry.contact.id}
            invite={invite}
          />
        </TableCell>
      )}
    </TableRow>
  )
}

WaitlistListItem.propTypes = {
  entry: PropTypes.shape({
    contact: PropTypes.shape({
      id: PropTypes.string.isRequired,
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
    }),
  }).isRequired,
  groupEventId: PropTypes.string,
  invite: PropTypes.object,
  invitesEnabled: PropTypes.bool,
}

const Waitlist = ({
  contacts,
  groupEventId,
  registrationInvites,
  invitesEnabled = false,
}) => {
  const inviteByContactId = getRegistrationInviteByContactId(
    registrationInvites,
  )
  const rows = contacts.map(entry => (
    <WaitlistListItem
      key={entry.contact.id}
      entry={entry}
      groupEventId={groupEventId}
      invite={inviteByContactId[entry.contact.id]}
      invitesEnabled={invitesEnabled}
    />
  ))

  return (
    <EnhancedTable
      rows={rows}
      columns={invitesEnabled ? columnsWithInvites : columns}
      label="Waitlist"
      variant="list"
      rowHeight="56px"
      showHeaderPagination={false}
      showPaginator={false}
      emptyState={() => null}
    />
  )
}

Waitlist.propTypes = {
  contacts: PropTypes.arrayOf(
    PropTypes.shape({
      contact: PropTypes.shape({
        id: PropTypes.string.isRequired,
        firstName: PropTypes.string.isRequired,
        lastName: PropTypes.string.isRequired,
        email: PropTypes.string.isRequired,
      }),
    }),
  ),
  groupEventId: PropTypes.string,
  registrationInvites: PropTypes.arrayOf(PropTypes.object),
  invitesEnabled: PropTypes.bool,
}

export default Waitlist
